import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiHome } from 'react-icons/fi';
import {
  MdAutoAwesome,
  MdTaskAlt,
  MdStorefront,
  MdLocalOffer,
  MdPersonOutline,
  MdInbox,
  MdViewKanban,
  MdPeopleOutline,
  MdHandshake,
  MdNotificationsActive,
  MdEvent,
  MdBarChart,
  MdCompareArrows,
  MdOutlineFolderCopy,
  MdScheduleSend,
  MdOutlineAttachEmail,
  MdHistory,
} from 'react-icons/md';
import { HiOutlineClipboardList } from 'react-icons/hi';
import { BsFolder } from 'react-icons/bs';
import { RiContactsLine, RiMoneyDollarCircleLine, RiBuildingLine, RiTerminalWindowLine, RiVideoLine, RiArrowDownSFill, RiArrowUpSFill } from 'react-icons/ri';
import { IoMegaphone, IoReaderOutline } from 'react-icons/io5';
import { LiaFileContractSolid } from 'react-icons/lia';
import { imageUrl } from '../../config/apiUrl';
import classes from './MenuV2.module.css';

/** A page link; `match` also lights it for the pages under it (a listing under Listings). */
function Item({ to, icon, label, badge, match }) {
  const path = useLocation()?.pathname || '';
  const on = path === to || (match && path.startsWith(match));
  return (
    <Link to={to} className={`${classes.item} ${on ? classes.on : ''}`} aria-current={on ? 'page' : undefined}>
      <span className={classes.icon}>{icon}</span>
      <span className={classes.label}>{label}</span>
      {badge > 0 && <span className={classes.badge}>{badge}</span>}
    </Link>
  );
}

function Group({ title, children, collapsible, startOpen = true }) {
  const [open, setOpen] = useState(startOpen);
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className={classes.group}>
      {title &&
        (collapsible ? (
          <button type="button" className={`${classes.groupTitle} ${classes.groupToggle}`} onClick={() => setOpen(!open)} aria-expanded={open}>
            {title}
            {open ? <RiArrowUpSFill size={16} /> : <RiArrowDownSFill size={16} />}
          </button>
        ) : (
          <div className={classes.groupTitle}>{title}</div>
        ))}
      {open && items}
    </div>
  );
}

/**
 * The side menu for the redesigned CRM (Michael, 23 Oct: the blue menu "looks a little
 * dated" beside the new pages). Same pages and the same who-sees-what rules as the
 * current menu, grouped by what people do, with the new pages first. The current
 * versions of redesigned pages stay reachable under "Current pages" until they retire.
 */
export default function MenuV2({ logo, user, unreadTodoCount, isPaidClient, allowed, superAdminEmail }) {
  const roles = user?.role || [];
  const isBroker = roles.includes('broker');
  const isHR = roles.includes('hr');
  const isAdmin = roles.includes('admin');
  const isSuper = roles.includes('super-admin');
  const staffDeals = isSuper || isAdmin || isBroker;
  const path = useLocation()?.pathname || '';
  const [cruds, setCruds] = useState(['/categories', '/industries', '/deadline-holidays'].includes(path));
  const sz = 18;

  return (
    <nav className={classes.menu} aria-label="Main menu">
      <div className={classes.logo}>
        <img src={imageUrl(logo)} alt="logo" />
      </div>
      <div className={classes.scroll}>
        <Group>
          <Item to="/" icon={<FiHome size={sz} />} label="Home" />
          <Item to="/bbs-ai" icon={<MdAutoAwesome size={sz} />} label="BBS AI" />
          <Item to="/v2/todo" icon={<MdTaskAlt size={sz} />} label="To-Do" badge={unreadTodoCount} />
        </Group>

        <Group title="Deals">
          {staffDeals && <Item to="/v2/listings" match="/v2/listings" icon={<MdStorefront size={sz} />} label="Listings" />}
          {staffDeals && <Item to="/v2/offers" icon={<MdLocalOffer size={sz} />} label="Offers" />}
          {!isHR && <Item to="/v2/interests" icon={<HiOutlineClipboardList size={sz} />} label="Interests" />}
          {staffDeals && <Item to="/v2/sellers" icon={<MdPersonOutline size={sz} />} label="Sellers" />}
          {staffDeals && <Item to="/v2/intake" icon={<MdInbox size={sz} />} label="New leads" />}
          {!isHR && <Item to="/team-folder" icon={<BsFolder size={sz} />} label="Deal Rooms" />}
          <Item to="/project-task-manager" icon={<MdViewKanban size={sz} />} label="Projects" />
        </Group>

        <Group title="People">
          <Item to="/contacts-new" icon={<RiContactsLine size={sz} />} label="Contacts" />
          <Item to="/leads" icon={<MdPeopleOutline size={sz} />} label="Leads" />
          {staffDeals && <Item to="/v2/partners" icon={<MdHandshake size={sz} />} label="Partners" />}
          {staffDeals && <Item to="/v2/buyer-alerts" icon={<MdNotificationsActive size={sz} />} label="Buyer alerts" />}
          <Item to="/zoom" icon={<MdEvent size={sz} />} label="Events" />
        </Group>

        <Group title="Money and reports">
          {staffDeals && <Item to="/v2/commission" icon={<RiMoneyDollarCircleLine size={sz} />} label="Commission" />}
          {staffDeals && <Item to="/v2/reports" icon={<MdBarChart size={sz} />} label="Reports" />}
          {staffDeals && <Item to="/v2/comps" icon={<MdCompareArrows size={sz} />} label="Comps" />}
        </Group>

        <Group title="Tools">
          <Item to="/documents" icon={<MdOutlineFolderCopy size={sz} />} label="Documents" />
          <Item to="/entities" icon={<RiBuildingLine size={sz} />} label="Entities" />
          {(isPaidClient || allowed) && (isAdmin || isBroker) && <Item to="/marketing" icon={<IoMegaphone size={sz} />} label="Marketing" />}
          {isSuper && <Item to="/automated-emails" icon={<MdOutlineAttachEmail size={sz} />} label="Automated Emails & SMS" />}
          <Item to="/trigger-emails" icon={<MdScheduleSend size={sz} />} label="Trigger Emails" />
          {allowed && <Item to="/contract-templates" icon={<LiaFileContractSolid size={sz} />} label="Contracts" />}
          {isSuper && <Item to="/contact-management" icon={<RiContactsLine size={sz} />} label="Contact Management" />}
          {isAdmin && (
            <button type="button" className={classes.item} onClick={() => setCruds(!cruds)} aria-expanded={cruds}>
              <span className={classes.icon}>
                <IoReaderOutline size={sz} />
              </span>
              <span className={classes.label}>CRUDS</span>
              {cruds ? <RiArrowUpSFill size={16} /> : <RiArrowDownSFill size={16} />}
            </button>
          )}
          {isAdmin && cruds && (
            <div className={classes.sub}>
              <Item to="/categories" label="Categories" />
              <Item to="/industries" label="Industries" />
              <Item to="/deadline-holidays" label="Deadline Holidays" />
            </div>
          )}
          {user?.email === superAdminEmail && <Item to="/token-management" icon={<RiTerminalWindowLine size={sz} />} label="Token Management" />}
          <Item to="/support-center" icon={<RiVideoLine size={sz} />} label="Support Center" />
        </Group>

        <Group title="Current pages" collapsible startOpen={['/listings', '/todo', '/commission', '/interests'].includes(path)}>
          <Item to="/listings" icon={<MdHistory size={sz} />} label="Listings (current)" />
          {!isHR && <Item to="/interests" icon={<MdHistory size={sz} />} label="Interests (current)" />}
          {!isHR && <Item to="/todo" icon={<MdHistory size={sz} />} label="To-Do (current)" />}
          {((isSuper || isBroker) && isPaidClient) || allowed ? <Item to="/commission" icon={<MdHistory size={sz} />} label="Commission (current)" /> : null}
        </Group>
      </div>
    </nav>
  );
}
